import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { dataService } from "../../../../../Services/data/data.service";
import { makeStyles } from "@material-ui/core/styles";
import Button from "../../../../Shared/Buttons/Button";
import { Select, SelectTrigger, SelectContent, SelectItem } from "../../../../Shared/Select";
import { Plus, Search } from "lucide-react";
import ResetFiltersButton from "../../../../Filter/ResetFiltersButton";
import { crudTypes, PriceDefinitionFilterTypes } from "../../../../../Config/constants";
import PriceDefinitionTable from "../PriceDefinition/PriceDefinitionTable";
import PriceDefinitionDialog from "../PriceDefinition/PriceDefinitionDialog";
import TablePagination from "@mui/material/TablePagination";
import useFilterPersistence from "../../../../../hooks/useFilterPersistence";

function PriceDefinitionTab(props) {
  const classes = useStyles();
  const { t } = useTranslation();

  // General
  const [originalPriceDefinitions, setOriginalPriceDefinitions] = useState([]); // Store the original list of price definitions - unfiltered
  const [priceDefinitionList, setPriceDefinitionList] = useState([]); // Filtered and sorted
  const [allCompanies, setAllCompanies] = useState([]);
  const { filters, setFilter, setFilterText, resetFilters, getFilterValue, hasActiveFilters, page, setPage, pageSize, setPageSize } = useFilterPersistence({
    pageKey: 'financial-price-definitions',
    filterSchema: { pd_search: PriceDefinitionFilterTypes.SEARCH, pd_company: PriceDefinitionFilterTypes.COMPANY, pd_account: PriceDefinitionFilterTypes.ACCOUNT },
    defaultPageSize: 25,
    persistPageSize: true,
  });

  // Price Definition Dialog
  const [selectedPriceDefinition, setSelectedPriceDefinition] = useState(null);
  const [priceDefinitionDialogOpen, setPriceDefinitionDialogOpen] = useState(false);
  const [crudType, setCrudType] = useState(null); // Create, Edit, View

  // Fetch Price Definitions
  useEffect(() => {
    dataService
      .getAllPriceDefinitionsWithEntries(props.Login.userid)
      .then((response) => {
        setOriginalPriceDefinitions(response);
      })
      .catch((error) => {
        console.error("Error fetching price definitions:", error);
        // Handle error, e.g., show a message to the user
      });
  }, [props.Login.userid]);

  // Fetch Companies
  useEffect(() => {
    if (props.Login && props.Login.userid) {
      dataService
        .getAllCompaniesByUserId(props.Login.userid)
        .then((response) => {
          setAllCompanies(response);
        })
        .catch((error) => {
          console.error("Error fetching companies:", error);
        });
    }
  }, [props.Login.userid]);

  const filterList = (thisFilters, priceDefinitions) => {
    let varFilteredlist = priceDefinitions ? priceDefinitions : [...originalPriceDefinitions];

    for (let i = 0; i < thisFilters.length; i++) {
      let filter = thisFilters[i];
      switch (filter.type) {
        case PriceDefinitionFilterTypes.SEARCH:
          varFilteredlist = varFilteredlist.filter((x) => x.name.toLowerCase().includes(filter.filterValue.toLowerCase()) || x.code.toLowerCase().includes(filter.filterValue.toLowerCase()));
          break;
        case PriceDefinitionFilterTypes.NAME:
          varFilteredlist = varFilteredlist.filter((x) => x.name.toLowerCase().includes(filter.filterValue.toLowerCase()));
          break;
        case PriceDefinitionFilterTypes.CODE:
          varFilteredlist = varFilteredlist.filter((x) => x.code.toLowerCase().includes(filter.filterValue.toLowerCase()));
          break;
        case PriceDefinitionFilterTypes.COMPANY:
          varFilteredlist = varFilteredlist.filter((x) => x.companyId === parseInt(filter.filterValue) || filter.filterValue === "");
          break;
        case PriceDefinitionFilterTypes.ACCOUNT:
          if (filter.filterValue === "none") {
            varFilteredlist = varFilteredlist.filter((x) => !x.defaultAccountId);
          } else if (filter.filterValue !== "") {
            varFilteredlist = varFilteredlist.filter((x) => x.defaultAccountId === parseInt(filter.filterValue));
          }
          break;
        default:
          varFilteredlist = varFilteredlist.filter((x) => x.name.toLowerCase().includes(filter.filterValue.toLowerCase()) || x.code.toLowerCase().includes(filter.filterValue.toLowerCase()));
          break;
      }
    }
    
    // Sort alphabetically by code
    varFilteredlist = varFilteredlist.sort((a, b) => a.code.localeCompare(b.code));
    
    if (priceDefinitions) {
      return varFilteredlist;
    } else {
      setPriceDefinitionList(varFilteredlist);
    }
  };

  // Re-apply filters whenever source data or active filters change (covers reset + URL restore)
  useEffect(() => {
    filterList(filters);
  }, [originalPriceDefinitions, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Create Price Definition Logic
  const openCreatePriceDefinitionDialog = () => {
    // Logic to open the dialog for creating a new price definition
    setCrudType(crudTypes.CREATE);
    setPriceDefinitionDialogOpen(true);
  };

  const closePriceDefinitionDialog = () => {
    // Logic to close the dialog for creating a new price definition
    setPriceDefinitionDialogOpen(false);
    setCrudType(null);
  };

  const handleEditDefinition = (priceDefinitionId) => {
    // Logic to open the dialog for editing an existing price definition
    const priceDefinition = originalPriceDefinitions.find((x) => x.id === priceDefinitionId);
    if (!priceDefinition) {
      console.log("Price definition not found.");
      return;
    }
    setSelectedPriceDefinition(priceDefinition);
    setCrudType(crudTypes.UPDATE);
    setPriceDefinitionDialogOpen(true);
  };

  const handleViewDefinition = (priceDefinitionId) => {
    // Logic to open the dialog for viewing an existing price definition

    const priceDefinition = originalPriceDefinitions.find((x) => x.id === priceDefinitionId);
    if (!priceDefinition) {
      console.log("Price definition not found.");
      return;
    }
    setSelectedPriceDefinition(priceDefinition);
    setCrudType(crudTypes.READ);
    setPriceDefinitionDialogOpen(true);
  };

  // Add this handler to add a new definition
  const handleAddPriceDefinition = (newDefinition) => {
    setOriginalPriceDefinitions((prev) => [...prev, newDefinition]);
  };

  // Add this handler to update an existing definition
  const handleUpdatePriceDefinition = (updatedDefinition) => {
    setOriginalPriceDefinitions((prev) => prev.map((def) => def.id === updatedDefinition.id ? updatedDefinition : def));
  };

  // Handler to remove a deleted definition from the lists
  const handleDeletePriceDefinition = (deletedDefinitionId) => {
    setOriginalPriceDefinitions((prev) => prev.filter((def) => def.id !== deletedDefinitionId));
  };

  // Utility function to get search value
  const getSearchValue = () => {
    return getFilterValue(PriceDefinitionFilterTypes.SEARCH);
  };

  // Utility function to get selected company
  const getSelectedCompany = () => {
    const companyId = getFilterValue(PriceDefinitionFilterTypes.COMPANY);
    return allCompanies.find((company) => company.id === parseInt(companyId)) || null;
  };

  // Unique accounts referenced by any price definition — derived from loaded data, no extra fetch needed
  const uniqueAccounts = Object.values(
    originalPriceDefinitions
      .filter((pd) => pd.defaultAccountId)
      .reduce((acc, pd) => {
        acc[pd.defaultAccountId] = { id: pd.defaultAccountId, name: pd.defaultAccountName };
        return acc;
      }, {})
  );

  const getSelectedAccountFilter = () => {
    const val = getFilterValue(PriceDefinitionFilterTypes.ACCOUNT);
    if (!val) return null;
    if (val === "none") return { id: "none", name: t('priceDefinitionTab.noAccountSet') };
    return uniqueAccounts.find((a) => a.id === parseInt(val)) || null;
  };

  // Calculate paginated rows
  const paginatedPriceDefinitions = priceDefinitionList.slice(page * pageSize, page * pageSize + pageSize);

  return (
    <React.Fragment>
      <div className={classes.tableManagementContainer}>
        <div className={classes.tableManagementContainer__start}>
          <div className={classes.searchContainer}>
            <input
              placeholder={t('priceDefinitionTab.searchPlaceholder')}
              className={classes.search__input}
              type="text"
              value={getFilterValue(PriceDefinitionFilterTypes.SEARCH)}
              onChange={(e) => setFilterText(PriceDefinitionFilterTypes.SEARCH, e.target.value)}
            />
            <Search size={16} className={classes.inputIcon} />
          </div>
          <div className={classes.filterContainer}>
            <Select value={getSelectedCompany()?.id} onChange={(company) => setFilter(PriceDefinitionFilterTypes.COMPANY, company?.id ?? "")} size="small">
              <SelectTrigger>{getSelectedCompany()?.name || t('priceDefinitionTab.allCompanies')}</SelectTrigger>
              <SelectContent>
                <SelectItem value={null}>{t('priceDefinitionTab.allCompanies')}</SelectItem>
                {allCompanies.map((company) => (
                  <SelectItem key={company.id} value={company}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className={classes.filterContainer}>
            <Select value={getSelectedAccountFilter()?.id} onChange={(val) => setFilter(PriceDefinitionFilterTypes.ACCOUNT, val ?? "")} size="small">
              <SelectTrigger>{getSelectedAccountFilter()?.name || t('priceDefinitionTab.allAccounts')}</SelectTrigger>
              <SelectContent>
                <SelectItem value={null}>{t('priceDefinitionTab.allAccounts')}</SelectItem>
                <SelectItem value="none">{t('priceDefinitionTab.noAccountSet')}</SelectItem>
                {uniqueAccounts.map((account) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {hasActiveFilters && <ResetFiltersButton onClick={resetFilters} />}
        </div>
        <div className={classes.tableManagementContainer__end}>
          <Button size="small" startIcon={<Plus />} onClick={() => openCreatePriceDefinitionDialog()}>
            {t('priceDefinitionTab.addPriceDefinition')}
          </Button>
        </div>
      </div>
      {/* Table */}
      <PriceDefinitionTable priceDefinitions={paginatedPriceDefinitions} handleEditDefinition={handleEditDefinition} handleViewDefinition={handleViewDefinition} searchValue={getSearchValue()} />

      {/* Pagination */}
      <TablePagination
        component="div"
        count={priceDefinitionList.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => setPageSize(parseInt(e.target.value, 10))}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />

      {/* Dialog */}
      <PriceDefinitionDialog
        dialogOpen={priceDefinitionDialogOpen}
        closeDialog={closePriceDefinitionDialog}
        crudType={crudType}
        priceDefinition={selectedPriceDefinition}
        onAdd={handleAddPriceDefinition}
        onUpdate={handleUpdatePriceDefinition}
        onDelete={handleDeletePriceDefinition}
        {...props}
      />
    </React.Fragment>
  );
}

export default PriceDefinitionTab;

const useStyles = makeStyles({
  tableManagementContainer: {
    width: "100%",
    height: "fit-content",

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    flexWrap: "wrap-reverse",

    marginBottom: "8px",
  },

  tableManagementContainer__start: {
    width: "fit-content",

    display: "flex",
    alignItens: "center",
    gap: "8px",
  },

  searchContainer: {
    width: "240px",
    position: "relative",
  },

  filterContainer: {
    width: "auto",
    minWidth: "fit-content",
  },

  search__input: {
    width: "100%",
    height: "32px",
    boxSizing: "border-box",

    display: "flex",
    alignItems: "center",

    padding: "0px 8px",
    paddingRight: "32px",

    borderRadius: "4px",
    border: "1px solid #DCDCDC",

    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    color: "#0C0E29",

    backgroundColor: "#FFFFFF",

    "&:focus": {
      outline: "none",
      border: "1px solid var(--color-primary)",
    },
  },

  inputIcon: {
    color: "#0C0E29",
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    right: "8px",
  },

  tableManagementContainer__end: {
    width: "fit-content",

    display: "flex",
    alignItens: "center",
    gap: "8px",
  },

  errorContainer: {
    width: "100%",
    backgroundColor: "#FDF3F3",

    border: "1px solid #FBE5E5",
    borderRadius: "6px",

    marginBottom: "16px",
    padding: "12px",
  },

  errorMessage: {
    fontSize: "16px",
    color: "#D84949",
  },

});
